import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { OverlayModule, type ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import {
  type AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  type ValidationErrors,
  type Validator,
} from '@angular/forms';
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  getMonth,
  getYear,
  isSameDay,
  isSameMonth,
  isToday,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ZagIcon } from '../icon/icon';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';
import { parseTypedDate, applyDateMask } from '../date-picker/date-picker';
import { LanguageService } from '../services/language.service';
import { FormField } from '../form-field/form-field';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

export interface DateRangeValue {
  start: string | null;
  end: string | null;
}

type DatePickerView = 'day' | 'month' | 'year' | 'decade';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;
const WEEKDAY_KEYS_MON_FIRST = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

interface DayCell {
  readonly date: Date;
  readonly dayOfMonth: number;
  readonly isCurrentMonth: boolean;
  readonly isToday: boolean;
  readonly isDisabled: boolean;
  readonly isRangeStart: boolean;
  readonly isRangeEnd: boolean;
  readonly isInRange: boolean;
  readonly isPreviewStart: boolean;
  readonly isPreviewEnd: boolean;
  readonly isInPreviewRange: boolean;
}

interface OptionCell {
  readonly label: string;
  readonly value: number;
  readonly isSelected: boolean;
  readonly isCurrent: boolean;
  readonly isDisabled: boolean;
  readonly isOutsideRange?: boolean;
}

type DisabledDateMatcher = (isoDate: string, date: Date) => boolean;

@Component({
  selector: 'zag-date-range-picker',
  imports: [OverlayModule, ZagIcon, ScrollIntoViewDirective, TranslatePipe, NgTemplateOutlet],
  templateUrl: './date-range-picker.html',
  styleUrl: './date-range-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePicker),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateRangePicker),
      multi: true,
    },
  ],
  host: {
    class: 'zag-date-range-picker',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class DateRangePicker implements ControlValueAccessor, Validator, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly languageService = inject(LanguageService);
  private readonly translateService = inject(TranslateService);
  private readonly document = inject(DOCUMENT);
  private readonly formField = inject(FormField, { optional: true });

  /** Returns a translator function; re-evaluates when language changes, making all dependents reactive. */
  private readonly translator = computed(() => {
    this.languageService.currentLanguage();
    return (key: string) => this.translateService.instant(key) as string;
  });

  @ViewChild(CdkConnectedOverlay) private connectedOverlay?: CdkConnectedOverlay;

  private rafId?: number;
  private lastOverlayWidth?: number;
  private readonly mobileQuery =
    typeof this.document.defaultView?.matchMedia === 'function' ? this.document.defaultView.matchMedia('(max-width: 539px)') : null;
  protected readonly isMobile = signal<boolean>(this.mobileQuery?.matches ?? false);
  private readonly onMobileChange = (e: MediaQueryListEvent) => {
    this.isMobile.set(e.matches);
    this.cdr.markForCheck();
  };

  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly required = input<boolean>(false);
  readonly clearable = input<boolean>(true);
  readonly min = input<string | undefined>(undefined);
  readonly max = input<string | undefined>(undefined);
  readonly disabledDates = input<readonly string[]>([]);
  readonly disabledDateMatcher = input<DisabledDateMatcher | undefined>(undefined);
  readonly startPlaceholder = input<string>('Start date');
  readonly endPlaceholder = input<string>('End date');
  readonly valueSeparator = input<string>(' - ');
  readonly openRangeLabel = input<string>('');
  readonly showSelectionHint = input<boolean>(false);
  readonly beforeOpen = input<(() => boolean) | undefined>(undefined);
  readonly recurring = input(false);
  /** When true, both inputs use a locale-aware date format mask (e.g. DD.MM.YYYY) */
  readonly maskInput = input<boolean>(false);
  /**
   * When true, the calendar panel is rendered inline (always visible, no
   * overlay, no trigger). Use this in dialogs / dedicated pickers where
   * the trigger-and-open UX is redundant. Selection state remains driven
   * by the same signals + `writeValue` / `valueChange` contract.
   */
  readonly inline = input<boolean>(false);

  protected readonly maskSeparator = computed(() => (this.languageService.currentLanguage() === 'en' ? '/' : '.'));
  protected readonly maskPlaceholder = computed(() => {
    const sep = this.maskSeparator();
    return `DD${sep}MM${sep}YYYY`;
  });

  protected readonly open = signal<boolean>(false);
  protected readonly viewMode = signal<DatePickerView>('day');
  protected readonly cursorDate = signal<Date>(new Date());
  protected readonly start = signal<string>('');
  protected readonly end = signal<string>('');
  protected readonly selectingStart = signal<boolean>(true);
  protected readonly previewIsoDate = signal<string>('');

  /** Label auto-detected from parent FormField for mobile overlay header */
  protected readonly fieldLabel = computed(() => this.formField?.label() ?? '');

  /** Pending start/end for mobile mode — staged until Apply is pressed */
  protected readonly pendingStart = signal<string>('');
  protected readonly pendingEnd = signal<string>('');
  protected readonly hasPendingRange = computed(() => !!this.pendingStart());

  // Typed input state
  protected readonly startInputText = signal<string>('');
  protected readonly endInputText = signal<string>('');
  protected readonly startInputError = signal<boolean>(false);
  protected readonly endInputError = signal<boolean>(false);

  private readonly inputValidation = computed(() => {
    const startText = this.startInputText().trim();
    const endText = this.endInputText().trim();
    const start = parseTypedDate(startText);
    const end = parseTypedDate(endText);
    const missingStart = this.required() && !startText;
    const missingEnd = this.required() && !endText;
    const invalidStart = !!startText && (!start || !this.isDateAllowed(start));
    const invalidEnd = !!endText && (!end || !this.isDateAllowed(end));
    const reversed = start !== null && end !== null && start > end;
    const errors: ValidationErrors = {};
    if (missingStart || missingEnd) errors['required'] = true;
    if (invalidStart) errors['dateRangeStart'] = true;
    if (invalidEnd) errors['dateRangeEnd'] = true;
    if (reversed) errors['dateRangeOrder'] = true;
    return {
      start,
      end,
      startError: missingStart || invalidStart || reversed,
      endError: missingEnd || invalidEnd || reversed,
      errors: Object.keys(errors).length > 0 ? errors : null,
    };
  });

  protected readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  /** On mobile the overlay is centred in the viewport via the panel class. */
  protected readonly mobileOverlayPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'center' },
  ];

  protected readonly startDate = computed(() => this.parseIsoDate(this.start()));
  protected readonly endDate = computed(() => this.parseIsoDate(this.end()));
  protected readonly previewDate = computed(() => this.parseIsoDate(this.previewIsoDate()));
  protected readonly minDate = computed(() => this.parseIsoDate(this.min()));
  protected readonly maxDate = computed(() => this.parseIsoDate(this.max()));
  protected readonly disabledDateSet = computed<Set<string>>(() => {
    const set = new Set<string>();
    for (const value of this.disabledDates()) {
      const parsed = this.parseIsoDate(value);
      if (parsed !== null) set.add(this.toIsoDate(parsed));
    }
    return set;
  });

  protected readonly headerLabel = computed(() => {
    const t = this.translator();
    const date = this.cursorDate();
    const mode = this.viewMode();
    if (mode === 'day') {
      const monthName = t(`breadcrumb.monthsFull.${MONTH_KEYS[getMonth(date)]}`);
      return `${monthName} ${getYear(date)}`;
    }
    if (mode === 'month') return String(getYear(date));
    if (mode === 'year') {
      const start = this.getYearGridStart(getYear(date)) + 1;
      return `${start}-${start + 9}`;
    }
    const start = this.getDecadeGridStart(getYear(date)) + 10;
    return `${start}-${start + 99}`;
  });

  protected readonly weekdayLabels = computed(() => {
    const t = this.translator();
    return WEEKDAY_KEYS_MON_FIRST.map(k => t(`breadcrumb.daysShort.${k}`));
  });

  protected readonly selectionHint = computed(() => (this.selectingStart() ? this.startPlaceholder() : this.endPlaceholder()));

  protected readonly showClearButton = computed(
    () =>
      this.clearable() &&
      (this.startDate() !== null ||
        this.endDate() !== null ||
        this.startInputText().trim().length > 0 ||
        this.endInputText().trim().length > 0) &&
      !this.disabled() &&
      !this.readonly()
  );

  protected readonly dayCells = computed((): DayCell[] => {
    const cursor = this.cursorDate();
    const rangeStart = this.startDate();
    const rangeEnd = this.endDate();
    const previewDate = this.previewDate();
    const isPreviewActive =
      rangeStart !== null && rangeEnd === null && !this.selectingStart() && previewDate !== null && !isSameDay(rangeStart, previewDate);
    const previewStart = isPreviewActive && previewDate < rangeStart ? previewDate : rangeStart;
    const previewEnd = isPreviewActive && previewDate > rangeStart ? previewDate : rangeStart;
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd }).map(day => ({
      date: day,
      dayOfMonth: day.getDate(),
      isCurrentMonth: isSameMonth(day, cursor),
      isToday: isToday(day),
      isDisabled: !this.isDateAllowed(day),
      isRangeStart: rangeStart !== null && isSameDay(rangeStart, day),
      isRangeEnd: rangeEnd !== null && isSameDay(rangeEnd, day),
      isInRange: rangeStart !== null && rangeEnd !== null && day > rangeStart && day < rangeEnd,
      isPreviewStart: isPreviewActive && previewStart !== null && isSameDay(previewStart, day),
      isPreviewEnd: isPreviewActive && previewEnd !== null && isSameDay(previewEnd, day),
      isInPreviewRange: isPreviewActive && previewStart !== null && previewEnd !== null && day > previewStart && day < previewEnd,
    }));
  });

  protected readonly monthCells = computed((): OptionCell[] => {
    const t = this.translator();
    const currentDate = new Date();
    const year = getYear(this.cursorDate());
    const selectedMonth = getMonth(this.startDate() ?? this.cursorDate());

    return MONTH_KEYS.map((key, month) => {
      const start = startOfMonth(new Date(year, month, 1));
      const end = endOfMonth(start);
      return {
        label: t(`breadcrumb.months.${key}`),
        value: month,
        isSelected: selectedMonth === month && getYear(this.cursorDate()) === year,
        isCurrent: getYear(currentDate) === year && getMonth(currentDate) === month,
        isDisabled: !this.isRangeSelectable(start, end),
      };
    });
  });

  protected readonly yearCells = computed((): OptionCell[] => {
    const selectedYear = getYear(this.startDate() ?? this.cursorDate());
    const currentYear = getYear(new Date());
    const start = this.getYearGridStart(getYear(this.cursorDate()));

    return Array.from({ length: 12 }, (_, index) => {
      const year = start + index;
      const rangeStart = new Date(year, 0, 1);
      const rangeEnd = new Date(year, 11, 31);
      return {
        label: String(year),
        value: year,
        isSelected: selectedYear === year,
        isCurrent: currentYear === year,
        isDisabled: !this.isRangeSelectable(rangeStart, rangeEnd),
        isOutsideRange: index === 0 || index === 11,
      };
    });
  });

  protected readonly decadeCells = computed((): OptionCell[] => {
    const selectedYear = getYear(this.startDate() ?? this.cursorDate());
    const currentYear = getYear(new Date());
    const start = this.getDecadeGridStart(getYear(this.cursorDate()));

    return Array.from({ length: 12 }, (_, index) => {
      const decadeStart = start + index * 10;
      const decadeEnd = decadeStart + 9;
      const rangeStart = new Date(decadeStart, 0, 1);
      const rangeEnd = new Date(decadeEnd, 11, 31);
      return {
        label: `${decadeStart}-${decadeEnd}`,
        value: decadeStart,
        isSelected: selectedYear >= decadeStart && selectedYear <= decadeEnd,
        isCurrent: currentYear >= decadeStart && currentYear <= decadeEnd,
        isDisabled: !this.isRangeSelectable(rangeStart, rangeEnd),
        isOutsideRange: index === 0 || index === 11,
      };
    });
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: DateRangeValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private validatorChange: () => void = () => {};

  constructor() {
    this.mobileQuery?.addEventListener('change', this.onMobileChange);

    effect(() => {
      if (this.disabled()) {
        this.open.set(false);
        this.stopPositionMonitoring();
        this.cdr.markForCheck();
      }
    });

    effect(() => {
      if (this.open() && !this.isMobile()) {
        this.startPositionMonitoring();
      } else {
        this.stopPositionMonitoring();
      }
    });

    effect(() => {
      this.maskSeparator(); // re-run when language changes so input separators update live
      untracked(() => {
        const startDate = parseTypedDate(this.startInputText().trim());
        const endDate = parseTypedDate(this.endInputText().trim());
        if (startDate) this.startInputText.set(this.formatDateForInput(startDate));
        if (endDate) this.endInputText.set(this.formatDateForInput(endDate));
      });
    });

    effect(() => {
      this.required();
      this.min();
      this.max();
      this.disabledDates();
      this.disabledDateMatcher();
      untracked(() => this.updateInputValidation());
    });
  }

  ngOnDestroy(): void {
    this.stopPositionMonitoring();
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
  }

  writeValue(value: DateRangeValue | null): void {
    // NgModel echoes our own non-empty committed value back; resetting would wipe typed-but-uncommitted text
    if ((value?.start || value?.end) && (value.start ?? '') === this.start() && (value.end ?? '') === this.end()) return;
    this.start.set(value?.start ?? '');
    this.end.set(value?.end ?? '');
    this.pendingEnd.set('');
    const startDate = this.parseIsoDate(value?.start ?? '');
    this.startInputText.set(startDate ? this.formatDateForInput(startDate) : '');
    this.resetEndInputText();
    this.updateInputValidation();
    this.cursorDate.set(startDate ?? new Date());
    this.cdr.markForCheck();
  }

  /** Format a date for display in the input field, respecting the current locale separator. */
  private formatDateForInput(date: Date): string {
    const sep = this.maskSeparator();
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = String(date.getFullYear());
    return `${d}${sep}${m}${sep}${y}`;
  }

  private resetEndInputText(): void {
    const end = this.endDate();
    this.endInputText.set(end ? this.formatDateForInput(end) : '');
  }

  registerOnChange(fn: (value: DateRangeValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.inputValidation().errors;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChange = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  protected onStartInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.maskInput()) {
      const sep = this.maskSeparator();
      const masked = applyDateMask(target.value, sep);
      this.startInputText.set(masked);
      target.value = masked;
    } else {
      this.startInputText.set(target.value);
    }
    this.updateInputValidation();
    // Live-update the calendar when the typed text is already a valid date
    const parsed = parseTypedDate(this.startInputText().trim());
    if (parsed && this.isDateAllowed(parsed)) {
      this.cursorDate.set(parsed);
    }
  }

  protected onEndInputChange(event: Event): void {
    this.pendingEnd.set('');
    const target = event.target as HTMLInputElement;
    if (this.maskInput()) {
      const sep = this.maskSeparator();
      const masked = applyDateMask(target.value, sep);
      this.endInputText.set(masked);
      target.value = masked;
    } else {
      this.endInputText.set(target.value);
    }
    this.updateInputValidation();
    // Live-update the calendar when the typed text is already a valid date
    const parsed = parseTypedDate(this.endInputText().trim());
    if (parsed && this.isDateAllowed(parsed)) {
      this.cursorDate.set(parsed);
    }
  }

  protected onStartInputBlur(): void {
    this.onTouched();
    this.commitStartInput();
  }

  protected onEndInputBlur(): void {
    this.onTouched();
    this.commitEndInput();
  }

  protected onStartInputKeyDown(event: KeyboardEvent): void {
    if (this.maskInput()) {
      this.handleMaskedKeyDown(
        event,
        () => this.commitStartInput(),
        () => this.openPanel(true)
      );
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitStartInput();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.open()) this.close();
    } else if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      if (!this.open()) this.openPanel(true);
    }
  }

  protected onEndInputKeyDown(event: KeyboardEvent): void {
    if (this.maskInput()) {
      this.handleMaskedKeyDown(
        event,
        () => this.commitEndInput(),
        () => this.openPanel(false)
      );
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitEndInput();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.open()) this.close();
    } else if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      if (!this.open()) this.openPanel(false);
    }
  }

  protected onStartInputFocus(): void {
    this.focusEndpoint(true);
  }

  protected onEndInputFocus(): void {
    this.focusEndpoint(false);
  }

  private focusEndpoint(forStart: boolean): void {
    this.openPanel(forStart);
    const el = this.document.activeElement as HTMLInputElement | null;
    if (el) {
      setTimeout(() => el.select(), 0);
    }
  }

  openPicker(): void {
    this.openPanel();
  }

  /** Handle keydown in mask mode: allow digits, navigation and control keys only. */
  private handleMaskedKeyDown(event: KeyboardEvent, onEnter: () => void, onOpen: () => void): void {
    const allowed = [
      'Backspace',
      'Delete',
      'Enter',
      'Escape',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowDown',
      'ArrowUp',
      'F4',
      'Home',
      'End',
    ];
    if (allowed.includes(event.key)) {
      if (event.key === 'Enter') {
        event.preventDefault();
        onEnter();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        if (this.open()) this.close();
      } else if (event.key === 'ArrowDown' || event.key === 'F4') {
        event.preventDefault();
        if (!this.open()) onOpen();
      }
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  protected openPanel(forStart = this.startDate() === null || this.endDate() !== null): void {
    if (this.disabled() || this.readonly()) return;
    if (this.open()) {
      this.selectingStart.set(forStart);
      return;
    }
    const beforeOpen = this.beforeOpen();
    if (beforeOpen && !beforeOpen()) return;
    this.selectingStart.set(forStart);
    this.viewMode.set('day');
    this.cursorDate.set(this.endDate() ?? this.startDate() ?? new Date());
    this.previewIsoDate.set('');
    this.open.set(true);
  }

  protected close(): void {
    this.discardPendingEnd();
    this.open.set(false);
    this.previewIsoDate.set('');
    this.onTouched();
  }

  protected onEscape(): void {
    if (this.open()) this.close();
  }

  protected clear(event: Event): void {
    event.stopPropagation();
    this.start.set('');
    this.end.set('');
    this.pendingEnd.set('');
    this.startInputText.set('');
    this.endInputText.set('');
    this.startInputError.set(false);
    this.endInputError.set(false);
    this.selectingStart.set(true);
    this.previewIsoDate.set('');
    this.open.set(false);
    this.emitValue();
    this.onTouched();
  }

  protected navigatePrevious(): void {
    const cursor = this.cursorDate();
    const mode = this.viewMode();
    if (mode === 'day') {
      this.cursorDate.set(addMonths(cursor, -1));
      return;
    }
    if (mode === 'month') {
      this.cursorDate.set(addYears(cursor, -1));
      return;
    }
    if (mode === 'year') {
      this.cursorDate.set(addYears(cursor, -10));
      return;
    }
    this.cursorDate.set(addYears(cursor, -100));
  }

  protected navigateNext(): void {
    const cursor = this.cursorDate();
    const mode = this.viewMode();
    if (mode === 'day') {
      this.cursorDate.set(addMonths(cursor, 1));
      return;
    }
    if (mode === 'month') {
      this.cursorDate.set(addYears(cursor, 1));
      return;
    }
    if (mode === 'year') {
      this.cursorDate.set(addYears(cursor, 10));
      return;
    }
    this.cursorDate.set(addYears(cursor, 100));
  }

  protected zoomOut(): void {
    const mode = this.viewMode();
    if (mode === 'day') {
      this.viewMode.set('month');
      return;
    }
    if (mode === 'month') {
      this.viewMode.set('year');
      return;
    }
    if (mode === 'year') {
      this.viewMode.set('decade');
    }
  }

  protected selectDay(day: DayCell): void {
    if (day.isDisabled) return;
    if (this.selectingStart()) {
      this.pickStart(day.date);
    } else {
      this.pickEnd(day.date);
    }
  }

  private pickStart(date: Date): void {
    this.applyPendingEnd();
    const end = this.endDate();
    this.start.set(this.toIsoDate(date));
    this.startInputText.set(this.formatDateForInput(date));
    if (end !== null && date > end) {
      this.end.set('');
      this.endInputText.set('');
    }
    this.finishPick(false);
  }

  private pickEnd(date: Date): void {
    const start = this.startDate();
    if (start === null) {
      this.stageEnd(date);
      return;
    }
    if (date < start) {
      this.selectingStart.set(true);
      return;
    }
    this.pendingEnd.set('');
    this.end.set(this.toIsoDate(date));
    this.endInputText.set(this.formatDateForInput(date));
    this.finishPick(true);
  }

  private stageEnd(date: Date): void {
    this.pendingEnd.set(this.toIsoDate(date));
    this.endInputText.set(this.formatDateForInput(date));
    this.selectingStart.set(true);
    this.previewIsoDate.set('');
    this.updateInputValidation();
  }

  private applyPendingEnd(): void {
    const pending = this.pendingEnd();
    if (!pending) return;
    this.end.set(pending);
    this.pendingEnd.set('');
  }

  private discardPendingEnd(): void {
    if (!this.pendingEnd()) return;
    this.pendingEnd.set('');
    this.resetEndInputText();
  }

  private finishPick(endPicked: boolean): void {
    this.startInputError.set(false);
    this.endInputError.set(false);
    this.selectingStart.set(endPicked);
    this.previewIsoDate.set('');
    if (this.isMobile()) {
      this.pendingStart.set(this.start());
      this.pendingEnd.set(this.end());
      this.updateInputValidation();
      return;
    }
    this.emitValue();
    if (endPicked && !this.inputValidation().errors) this.close();
  }

  /** Apply the pending range (mobile only) */
  protected applyMobile(): void {
    this.emitValue();
    this.pendingStart.set('');
    this.pendingEnd.set('');
    this.close();
  }

  /** Close without applying (mobile cancel) */
  protected closeMobile(): void {
    this.pendingStart.set('');
    this.pendingEnd.set('');
    // Revert to last committed values
    const startDate = this.parseIsoDate(this.start());
    this.startInputText.set(startDate ? this.formatDateForInput(startDate) : '');
    this.resetEndInputText();
    this.updateInputValidation();
    this.close();
  }

  protected setPreviewDate(day: DayCell): void {
    if (day.isDisabled || this.selectingStart() || this.startDate() === null || this.endDate() !== null) return;
    this.previewIsoDate.set(this.toIsoDate(day.date));
  }

  protected clearPreviewDate(): void {
    this.previewIsoDate.set('');
  }

  protected selectMonth(option: OptionCell): void {
    if (option.isDisabled) return;
    this.cursorDate.set(setMonth(this.cursorDate(), option.value));
    this.viewMode.set('day');
  }

  protected selectYear(option: OptionCell): void {
    if (option.isDisabled) return;
    this.cursorDate.set(setYear(this.cursorDate(), option.value));
    this.viewMode.set('month');
  }

  protected selectDecade(option: OptionCell): void {
    if (option.isDisabled) return;
    this.cursorDate.set(setYear(this.cursorDate(), option.value));
    this.viewMode.set('year');
  }

  private commitStartInput(): void {
    this.commitInput('start');
  }

  private commitEndInput(): void {
    this.commitInput('end');
  }

  private commitInput(field: 'start' | 'end'): void {
    this.updateInputValidation();
    const validation = this.inputValidation();
    if (validation.errors) return;
    const { start, end } = validation;
    if (!start && this.pendingEnd() && !this.pendingStart()) return;
    this.start.set(start ? this.toIsoDate(start) : '');
    this.end.set(end ? this.toIsoDate(end) : '');
    this.pendingEnd.set('');
    this.startInputText.set(start ? this.formatDateForInput(start) : '');
    this.endInputText.set(end ? this.formatDateForInput(end) : '');
    const selectedDate = validation[field];
    if (selectedDate) this.cursorDate.set(selectedDate);
    this.emitValue();
  }

  private updateInputValidation(): void {
    const validation = this.inputValidation();
    this.startInputError.set(validation.startError);
    this.endInputError.set(validation.endError);
    this.validatorChange();
  }

  private emitValue(): void {
    this.updateInputValidation();
    if (this.inputValidation().errors) return;
    this.onChange({
      start: this.start() || null,
      end: this.end() || null,
    });
  }

  private parseIsoDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const parts = value.split('-').map(Number);
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    if (getYear(date) !== year || getMonth(date) !== month - 1 || date.getDate() !== day) return null;
    return date;
  }

  private toIsoDate(date: Date): string {
    const year = String(getYear(date));
    const month = String(getMonth(date) + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getYearGridStart(year: number): number {
    return Math.floor(year / 10) * 10 - 1;
  }

  private getDecadeGridStart(year: number): number {
    return Math.floor(year / 100) * 100 - 10;
  }

  private isDateAllowed(date: Date): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min !== null && date < min) return false;
    if (max !== null && date > max) return false;
    if (this.isDateExplicitlyDisabled(date)) return false;
    return true;
  }

  private isRangeSelectable(start: Date, end: Date): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min !== null && end < min) return false;
    if (max !== null && start > max) return false;
    const effectiveStart = min !== null && start < min ? min : start;
    const effectiveEnd = max !== null && end > max ? max : end;
    if (effectiveStart > effectiveEnd) return false;
    return eachDayOfInterval({ start: effectiveStart, end: effectiveEnd }).some(day => !this.isDateExplicitlyDisabled(day));
  }

  private isDateExplicitlyDisabled(date: Date): boolean {
    const isoDate = this.toIsoDate(date);
    if (this.disabledDateSet().has(isoDate)) return true;
    const matcher = this.disabledDateMatcher();
    return matcher?.(isoDate, date) ?? false;
  }

  private startPositionMonitoring(): void {
    const check = () => {
      if (!this.open()) return;
      const overlayRef = this.connectedOverlay?.overlayRef;
      const wrapper = this.hostRef.nativeElement.querySelector('.zag-date-range-picker__trigger-wrapper') as HTMLElement | null;
      if (overlayRef && wrapper) {
        const w = Math.round(wrapper.getBoundingClientRect().width);
        if (this.lastOverlayWidth !== w) {
          overlayRef.updateSize({ minWidth: w });
          this.lastOverlayWidth = w;
        }
        overlayRef.updatePosition();
      }
      this.rafId = requestAnimationFrame(check);
    };
    this.rafId = requestAnimationFrame(check);
  }

  private stopPositionMonitoring(): void {
    if (this.rafId !== undefined) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
    this.lastOverlayWidth = undefined;
  }
}
